/**
 * usePickerLayout — which one-line shape of the candidate panel's report
 * picker fits its row (Jose, 2026-10-27: the side panel never wraps it).
 *
 * Every shape is one bordered box holding the report choice and Download:
 * - `wide`: the captioned radios, a divider, [⬇ Download PDF | ▾];
 * - `medium`: the same radios without captions or divider, [⬇ PDF | ▾];
 * - `narrow`: one native select of the four reports, [⬇ Download PDF | ▾].
 *
 * THE THRESHOLDS ARE A FIRST GUESS, AND THE ROW CORRECTS THEM (/pr-review
 * #1102 iter 1). The starting values are each shape's width measured in
 * Chromium, light theme, with the box around Download (/pr-review #1105:
 * four kinds, two-digit referee counts, "Download PDF": wide 658px, medium
 * 545px) plus a margin of about 45px. Content can be wider than that —
 * "Download Word", three-digit counts, the light theme's larger label,
 * a bigger browser font — and a fixed number cannot know. So after every
 * render the row checks itself: if the shape it shows overflows, the width
 * it actually needs is recorded and the next shape down is used. A recorded
 * need only grows, so a later widening steps back up only once it truly fits.
 *
 * A recorded need never shrinks while the panel stays on one candidate: after
 * "Download Word" pushed the row to `medium`, picking PDF keeps `medium`
 * until the next candidate. That errs toward never overflowing. The check
 * runs on each render of the panel; content that widens without one (a late
 * web font) is caught at the next render.
 *
 * Unmeasured (happy-dom, a hidden panel) reads as `wide`.
 */
import { type RefCallback, useCallback, useLayoutEffect, useState } from "react";

import { useElementWidth } from "./charts/useElementWidth";

export type PickerLayout = "wide" | "medium" | "narrow";

export const PICKER_WIDE_MIN = 705;
export const PICKER_MEDIUM_MIN = 590;

/** Room kept past a measured overflow, so a one-pixel change does not flip it back. */
const OVERFLOW_SLACK = 8;

export function usePickerLayout(): { ref: RefCallback<HTMLElement>; layout: PickerLayout } {
  const measured = useElementWidth(0);
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [need, setNeed] = useState({ wide: PICKER_WIDE_MIN, medium: PICKER_MEDIUM_MIN });
  const measuredRef = measured.ref;
  const ref = useCallback<RefCallback<HTMLElement>>(
    (node) => {
      measuredRef(node);
      setElement(node);
    },
    [measuredRef]
  );

  const width = measured.width;
  const layout: PickerLayout =
    width === 0 ? "wide" : width >= need.wide ? "wide" : width >= need.medium ? "medium" : "narrow";

  // After every render: does the shape on screen fit? `narrow` is the floor.
  useLayoutEffect(() => {
    if (!element || layout === "narrow") return;
    const overflow = element.scrollWidth - element.clientWidth;
    if (overflow <= 1) return;
    const required = element.scrollWidth + OVERFLOW_SLACK;
    setNeed((current) =>
      current[layout] >= required ? current : { ...current, [layout]: required }
    );
  });

  return { ref, layout };
}
